// Block layer: how one block member is written into a slot of the Draft.
// The pure helpers behind applyBlock (programBlocksExpand.ts), split out of
// that file for the 500-line cap; cellFromBlockRow also carries amrapStop (#355).
import {
  blankCell,
  rawForReplacedText,
  type CellDraft,
  type SetRowDraft,
  type SlotDraft,
} from "./programEdit.js";
import type { BlockGroup, BlockMember, BlockSetRow } from "./programBlocks.js";

// One block row → one matrix cell. Both expansion paths (write back into
// an existing slot, and build a fresh slot) land here, so the time axis
// can never survive on one path and be dropped on the other.
const cellFromBlockRow = (br: BlockSetRow): CellDraft => {
  const cell: CellDraft = { reps: br.reps, intensityValue: br.intensityValue };
  if (br.amrap) cell.amrap = true;
  if (br.workDurationSec !== undefined)
    cell.workDurationSec = br.workDurationSec;
  if (br.restSec !== undefined) cell.restSec = br.restSec;
  if (br.repsMin !== undefined) cell.repsMin = br.repsMin;
  if (br.repsMax !== undefined) cell.repsMax = br.repsMax;
  if (br.amrapStop) cell.amrapStop = { ...br.amrapStop };
  if (br.pausePhase !== undefined) cell.pausePhase = br.pausePhase;
  if (br.pauseSec !== undefined) cell.pauseSec = br.pauseSec;
  return cell;
};

// Same set count AND same per-row intensity kind: the block's rows can be
// written per-week into the existing matrix without disturbing other weeks.
//
// ⚠️ workKind belongs in this test for a reason the intensity kind makes
// easy to miss: it lives on the ROW, shared by every week. Merging a reps
// block into the complementary weeks of a timed slot would therefore
// rewrite the shape of the weeks it never touched: the W1-W3 bouts would
// quietly become reps sets. Different shape ⇒ a new slot, not a merge.
//
// #336: a row also takes a kind one of its rounds already holds (kg W1-3,
// RIR W4), so editing that round keeps the slot. A kind it has nowhere is
// still a new slot.
export const sameRowShape = (slot: SlotDraft, member: BlockMember): boolean =>
  slot.setRows.length === member.setRows.length &&
  slot.setRows.every((r, i) => {
    const kind = member.setRows[i]!.intensityKind;
    return (
      (r.intensityKind === kind || r.cells.some((c) => c.kind === kind)) &&
      (r.workKind ?? "") === (member.setRows[i]!.workKind ?? "")
    );
  });

export const coversAllActive = (slot: SlotDraft, weeks: number[]): boolean =>
  slot.inactiveWeeks.every((off, w) => off || weeks.includes(w));

const slotFields = (member: BlockMember) => ({
  exerciseId: member.exerciseId,
  exerciseName: member.exerciseName,
  instructions: member.instructions,
  amrapLastSet: member.amrapLastSet,
  maxAttempt: member.maxAttempt,
  rpeEnabled: member.rpeEnabled,
  rirEnabled: member.rirEnabled,
});

// Write the member's prescription into the block's weeks of an existing,
// shape-compatible slot. Other weeks' cells stay untouched.
export const writeWeeks = (
  slot: SlotDraft,
  member: BlockMember,
  weeks: number[],
): SlotDraft => ({
  ...slot,
  ...slotFields(member),
  // PE-21: the block REPLACES this slot's cue text. Any translation
  // behind the old text describes the old text, so it is dropped unless
  // the write leaves the string exactly as it was. See rawForReplacedText.
  instructionsRaw: rawForReplacedText(
    slot.instructions,
    member.instructions,
    slot.instructionsRaw,
  ),
  setRows: slot.setRows.map((row, r): SetRowDraft => {
    const br = member.setRows[r]!;
    const cells = row.cells.map((c) => ({ ...c }));
    const notesPerCell = [...row.notesPerCell];
    const notesRawPerCell = row.notesRawPerCell
      ? [...row.notesRawPerCell]
      : undefined;
    for (const w of weeks) {
      const cell = cellFromBlockRow(br);
      // Not the row's axis: the round keeps its own (#336).
      if (br.intensityKind !== row.intensityKind) cell.kind = br.intensityKind;
      cells[w] = cell;
      if (notesRawPerCell)
        notesRawPerCell[w] = rawForReplacedText(
          notesPerCell[w] ?? "",
          br.note,
          notesRawPerCell[w],
        );
      notesPerCell[w] = br.note;
    }
    const next: SetRowDraft = {
      ...row,
      cells,
      notesPerCell,
      ...(notesRawPerCell ? { notesRawPerCell } : {}),
    };
    // The BLOCK's shape wins over the slot's: spreading `row` would keep
    // a stale workKind after the author turned a timed set back into a
    // reps set, and the row would then emit a bout nobody prescribed.
    if (br.workKind) next.workKind = br.workKind;
    else delete next.workKind;
    return next;
  }),
  inactiveWeeks: slot.inactiveWeeks.map((off, w) =>
    weeks.includes(w) ? false : off,
  ),
});

// Build a fresh slot for the member: block weeks carry the prescription,
// the rest are inactive placeholders. variantsRaw stays empty: buildPayload
// synthesises the single every-week variant, which is exactly what a
// composable slot stores.
export const slotFromMember = (
  member: BlockMember,
  weeks: number[],
  cycleWeeks: number,
  id: string,
  group: BlockGroup | null,
): SlotDraft => ({
  id,
  ...slotFields(member),
  setRows: member.setRows.map((br) => ({
    intensityKind: br.intensityKind,
    ...(br.workKind ? { workKind: br.workKind } : {}),
    cells: Array.from({ length: cycleWeeks }, (_, w) =>
      weeks.includes(w) ? cellFromBlockRow(br) : blankCell(),
    ),
    notesPerCell: Array.from({ length: cycleWeeks }, (_, w) =>
      weeks.includes(w) ? br.note : "",
    ),

  })),
  group,
  variantsRaw: [],
  inactiveWeeks: Array.from({ length: cycleWeeks }, (_, w) => !weeks.includes(w)),
});
